import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField, type FloatingFieldColors } from "./FloatingField";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useCatalogPayers, useCatalogStates } from "../hooks/useCatalog";
import { resolveStateOptions, stateName } from "../utils/states";
import { PayoutMethodType, type PayoutInput } from "../interfaces/payout";
import type { EstimateOption } from "../api/pricing";
import type { RecipientDestination } from "../api/recipients";
import {
  PayoutCardValidationError,
  preValidatePayoutCard,
} from "../utils/pre-validate-payout-card";
import {
  CARDHOLDER_NAME_INPUT_MAX_LENGTH,
  formatCardholderNameInput,
  isValidCardholderName,
} from "../utils/cardholderName";
import {
  bareStateCode,
  compatibleBookDestinations,
  destinationListKey,
  type CompatibleBookDestination,
} from "../utils/recipient-destinations";
import {
  catalogPayerDisplayName,
  dedupeOfferableCashPayers,
  dedupeOfferableCashPayersForState,
  payerCodesEquivalent,
  resolveCashPickupOption,
} from "../utils/payerCode";
import {
  bankPrefixMismatch,
  inspectBankAccountNumber,
} from "../utils/bankAccountNumber";
import { BackArrowLight, CloseLight } from "../assets/navIcons";

const MONO_INPUT: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  letterSpacing: "0.04em",
};

function payoutFieldColors(view: {
  background: string;
  title: string;
  bodyCopy: string;
  subtitile: string;
  errorColor?: string;
}): FloatingFieldColors {
  return {
    background: view.background,
    title: view.title,
    bodyCopy: view.bodyCopy,
    inactiveColor: view.subtitile,
    errorColor: view.errorColor,
  };
}

export interface PayoutDetailsResult {
  payout: PayoutInput;
  /** Pre-quote option the selection priced against, when one matched. */
  option: EstimateOption | null;
}

export interface PayoutDetailsSlotProps {
  method: PayoutMethodType;
  destinationCountry: string;
  /** ISO 4217 destination currency for catalog payer queries. */
  destinationCurrency?: string | null;
  /**
   * Sender funding rail — scoped into catalog states/payers so we don't mix
   * CRB_INTERMEX state lists with INCOMM ANY:ANY cash-deposit routes.
   */
  fundingMethod?: string | null;
  /** Origin country for the same catalog scope (optional but preferred). */
  originCountryCode?: string | null;
  /** Pre-quote options for the corridor — drives payer offerability + SKU. */
  options: EstimateOption[];
  optionsLoading?: boolean;
  /** Habitual destinations from a recipient-book pick (may be empty). */
  bookDestinations?: RecipientDestination[];
  /** Existing payout to seed the form when re-editing. */
  value?: PayoutInput | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: PayoutDetailsResult) => void;
}

/** Local account length expected per destination country (digits only). */
const ACCOUNT_LENGTH: Record<string, number> = { MX: 18, AR: 22, PE: 20 };
const MAX_ACCOUNT_NUMBER_LENGTH = 34;

/** The catalog payer code a built payout carries, if any. */
const payerCodeOf = (
  method: PayoutMethodType,
  payout: PayoutInput
): string | null => {
  switch (method) {
    case PayoutMethodType.BANK_ACCOUNT:
      return payout.bankId ?? null;
    case PayoutMethodType.CASH_PICKUP:
      return payout.payerId ?? null;
    case PayoutMethodType.MOBILE_WALLET:
      return payout.carrierId ?? null;
    default:
      return null;
  }
};

/**
 * Resolves the exact pre-quote option for a selection: the one whose payerCode
 * matches the chosen payer, falling back to the single option for the method
 * (bank/card corridors expose one generic SKU rather than one-per-payer).
 * Cash pickup binds the SKU that covers the selected state (ANY:ANY or
 * matching subdivision) — never a different state's CRB row.
 */
const resolveOption = (
  method: PayoutMethodType,
  payout: PayoutInput,
  options: EstimateOption[]
): EstimateOption | null => {
  if (method === PayoutMethodType.CASH_PICKUP && payout.payerId) {
    return resolveCashPickupOption(options, payout.payerId, payout.stateCode);
  }
  const forMethod = options.filter((o) => o.payoutMethod === method);
  if (forMethod.length === 0) return null;
  const payerCode = payerCodeOf(method, payout);
  if (payerCode) {
    const byPayer = forMethod.find((o) => o.payerCode === payerCode);
    if (byPayer) return byPayer;
    if (method === PayoutMethodType.MOBILE_WALLET) {
      return null;
    }
  }
  // Saved bank/card instruments use the corridor's method-level SKU.
  return forMethod[0] ?? null;
};

/**
 * Second stage of the payout picker: define bank / cash / wallet, or enter a
 * new destination card (PAN). Saved PUSH_TO_CARD and BANK_ACCOUNT book entries
 * live on the first popup — do not remount them here.
 */
export const PayoutDetailsSlot: React.FC<PayoutDetailsSlotProps> = ({
  method,
  destinationCountry,
  destinationCurrency,
  fundingMethod,
  originCountryCode,
  options,
  optionsLoading,
  bookDestinations = [],
  value,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy } = colorConfig;

  const savedEntries = useMemo(
    () => compatibleBookDestinations(bookDestinations, method, options),
    [bookDestinations, method, options]
  );

  const submit = (payout: PayoutInput): void => {
    onSubmit({ payout, option: resolveOption(method, payout, options) });
  };

  const submitSaved = (entry: CompatibleBookDestination): void => {
    const option =
      options.find((o) => o.capabilityKey === entry.capabilityKey) ??
      resolveOption(method, entry.payout, options);
    onSubmit({ payout: entry.payout, option });
  };

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t(`payout.details.${method}.title`)}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      {(method === PayoutMethodType.CASH_PICKUP ||
        method === PayoutMethodType.MOBILE_WALLET) &&
        savedEntries.length > 0 && (
        <div className="payout-form">
          <SavedDestinationsPicker
            entries={savedEntries}
            method={method}
            destinationCountry={destinationCountry}
            destinationCurrency={destinationCurrency}
            fundingMethod={fundingMethod}
            originCountryCode={originCountryCode}
            onSelect={submitSaved}
          />
        </div>
      )}

      {method === PayoutMethodType.BANK_ACCOUNT && (
        <BankAccountForm
          destinationCountry={destinationCountry}
          destinationCurrency={destinationCurrency}
          fundingMethod={fundingMethod}
          originCountryCode={originCountryCode}
          value={value}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.CASH_PICKUP && (
        <CashPickupForm
          destinationCountry={destinationCountry}
          destinationCurrency={destinationCurrency}
          fundingMethod={fundingMethod}
          originCountryCode={originCountryCode}
          options={options}
          optionsLoading={optionsLoading}
          value={value}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.PUSH_TO_CARD && (
        <CardForm
          value={value}
          sourceCountry={originCountryCode || "US"}
          destinationCountry={destinationCountry}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.MOBILE_WALLET && (
        <MobileWalletForm
          destinationCountry={destinationCountry}
          destinationCurrency={destinationCurrency}
          fundingMethod={fundingMethod}
          originCountryCode={originCountryCode}
          value={value}
          onSubmit={submit}
        />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Saved book destinations
// ---------------------------------------------------------------------------

interface SavedDestinationsPickerProps {
  entries: CompatibleBookDestination[];
  method: PayoutMethodType;
  destinationCountry: string;
  destinationCurrency?: string | null;
  fundingMethod?: string | null;
  originCountryCode?: string | null;
  onSelect: (entry: CompatibleBookDestination) => void;
}

const SAVED_SKELETON_ROWS = 2;

const SavedListSkeleton = () => (
  <>
    {Array.from({ length: SAVED_SKELETON_ROWS }, (_, i) => (
      <div key={i} className="payout-saved-skeleton" aria-hidden="true">
        <span className="payout-saved-skeleton-line" />
        <span className="payout-saved-skeleton-line short" />
      </div>
    ))}
  </>
);

const SavedDestinationsPicker: React.FC<SavedDestinationsPickerProps> = ({
  entries,
  method,
  destinationCountry,
  destinationCurrency,
  fundingMethod,
  originCountryCode,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { data: payers, loading: payersLoading } = useCatalogPayers({
    destinationCountryCode: destinationCountry,
    payoutMethod: method,
    ...(originCountryCode ? { originCountryCode } : {}),
    ...(fundingMethod ? { fundingMethod } : {}),
    ...(destinationCurrency ? { currencyCode: destinationCurrency } : {}),
  });

  const labeled = entries
    .map((entry) => {
      const { payout } = entry;
      if (method === PayoutMethodType.CASH_PICKUP) {
        const bare = bareStateCode(payout.stateCode);
        const stateLabel = bare
          ? stateName(destinationCountry, bare) || bare
          : undefined;
        const payerLabel = catalogPayerDisplayName(payout.payerId, payers);
        if (!payerLabel || !stateLabel) return null;
        return { entry, title: payerLabel, subtitle: stateLabel };
      }
      if (method === PayoutMethodType.MOBILE_WALLET) {
        return {
          entry,
          title: payout.phone || t("payout.methods.MOBILE_WALLET"),
          subtitle: catalogPayerDisplayName(payout.carrierId, payers) || undefined,
        };
      }
      return null;
    })
    .filter((row): row is NonNullable<typeof row> => row != null);

  const showBlock = payersLoading || labeled.length > 0;
  if (!showBlock) return null;

  return (
    <section
      className="payout-saved"
      aria-label={t("payout.savedDestinations.aria")}
      aria-busy={payersLoading}
    >
      <span className="payout-field-label">
        {t("payout.savedDestinations.label")}
      </span>
      <div role="list" className="payout-saved-list">
        {payersLoading ? (
          <SavedListSkeleton />
        ) : (
          labeled.map(({ entry, title, subtitle }) => (
            <button
              key={destinationListKey(entry.destination)}
              type="button"
              role="listitem"
              className="payout-saved-item"
              onClick={() => onSelect(entry)}
            >
              <span className="payout-saved-title">{title}</span>
              {subtitle && <span className="payout-hint">{subtitle}</span>}
            </button>
          ))
        )}
      </div>
      <span className="payout-hint payout-or-new">
        {t("payout.savedDestinations.orNew")}
      </span>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Sub-forms
// ---------------------------------------------------------------------------

const sanitizeDigits = (raw: string, length?: number): string => {
  const digits = raw.replace(/\D/g, "");
  return length != null ? digits.slice(0, length) : digits;
};

const sanitizeAccountNumber = (raw: string, exactLength?: number): string => {
  if (exactLength != null) {
    return sanitizeDigits(raw, exactLength);
  }
  return raw
    .replace(/\s/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase()
    .slice(0, MAX_ACCOUNT_NUMBER_LENGTH);
};

const groupDigits = (digits: string): string =>
  digits.replace(/(\d{4})(?=\d)/g, "$1 ");

interface SubFormProps {
  destinationCountry: string;
  destinationCurrency?: string | null;
  fundingMethod?: string | null;
  originCountryCode?: string | null;
  value?: PayoutInput | null;
  onSubmit: (payout: PayoutInput) => void;
}

/** Catalog query scope shared by payout sub-forms (mirrors smart). */
const catalogScopeOf = (
  fundingMethod?: string | null,
  originCountryCode?: string | null,
  currencyCode?: string | null
) => ({
  ...(originCountryCode ? { originCountryCode } : {}),
  ...(fundingMethod ? { fundingMethod } : {}),
  ...(currencyCode ? { currencyCode } : {}),
});

const BankAccountForm: React.FC<SubFormProps> = ({
  destinationCountry,
  destinationCurrency,
  fundingMethod,
  originCountryCode,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy, errorColor } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const { data: payers, loading } = useCatalogPayers({
    ...catalogScopeOf(fundingMethod, originCountryCode, destinationCurrency),
    destinationCountryCode: destinationCountry,
    payoutMethod: "BANK_ACCOUNT",
  });
  const banks = useMemo(
    () =>
      (payers ?? [])
        .filter((p) => p.effectiveStatus === "AVAILABLE" && p.displayName?.trim())
        .map((p) => ({
          id: p.payerCode,
          name: p.displayName.trim(),
          accountPrefixes: p.accountPrefixes,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [payers]
  );

  const [bankId, setBankId] = useState<string | null>(value?.bankId ?? null);
  const [accountNumber, setAccountNumber] = useState<string>(
    value?.accountNumber ?? ""
  );
  const expectedLength = ACCOUNT_LENGTH[destinationCountry.toUpperCase()];
  const identifier = sanitizeAccountNumber(accountNumber, expectedLength);
  const inspection = inspectBankAccountNumber(destinationCountry, identifier);
  const selectedPrefixes = banks.find((b) => b.id === bankId)?.accountPrefixes;
  const mismatch =
    inspection.canContinue &&
    bankPrefixMismatch(inspection.bankCode, selectedPrefixes);
  const valid = Boolean(bankId) && inspection.canContinue;

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !bankId) return;
        onSubmit({ source: "new", bankId, accountNumber: identifier });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.bank")}
          value={bankId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={bankId ?? ""}
            onChange={(e) => {
              setBankId(e.target.value || null);
            }}
          >
            <option value="" />
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {!loading && banks.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noBanks")}</span>
        )}
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.accountNumber")}
          value={identifier}
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode={expectedLength == null ? "text" : "numeric"}
            maxLength={expectedLength ?? MAX_ACCOUNT_NUMBER_LENGTH}
            autoComplete="off"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={identifier}
            onChange={(e) => {
              setAccountNumber(
                sanitizeAccountNumber(e.target.value, expectedLength)
              );
            }}
          />
        </FloatingField>
        {expectedLength != null && (
          <span className="payout-hint">
            {identifier.length} / {expectedLength}
          </span>
        )}
        {identifier.length > 0 && !inspection.formatOk && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.accountNumberInvalid")}
          </span>
        )}
        {mismatch && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.bankMismatch")}
          </span>
        )}
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {mismatch
          ? t("payout.fields.bankMismatchConfirm")
          : t("common.continue")}
      </Button>
    </form>
  );
};

interface CashPickupFormProps extends SubFormProps {
  options: EstimateOption[];
  optionsLoading?: boolean;
}

const CashPickupForm: React.FC<CashPickupFormProps> = ({
  destinationCountry,
  destinationCurrency,
  fundingMethod,
  originCountryCode,
  options,
  optionsLoading,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);

  const catalogScope = catalogScopeOf(
    fundingMethod,
    originCountryCode,
    destinationCurrency
  );

  // `fundingMethod` scopes states to the corridor that can actually fund the
  // send (e.g. CASH_DEPOSIT → INCOMM ANY:ANY). Without it, CRB_INTERMEX
  // state-scoped MX_* payers pollute the picker and intersect to empty.
  const { data: states, loading: statesLoading } = useCatalogStates(
    destinationCountry,
    { payoutMethod: "CASH_PICKUP", ...catalogScope }
  );

  // Country-wide cash payers. When the partner only has state-agnostic payers
  // the states endpoint returns nothing, so we fall back to the full ISO list.
  const { data: countryPayers, loading: countryPayersLoading } =
    useCatalogPayers({
      ...catalogScope,
      destinationCountryCode: destinationCountry,
      payoutMethod: "CASH_PICKUP",
    });

  const availableCountryPayers = useMemo(
    () => (countryPayers ?? []).filter((p) => p.effectiveStatus === "AVAILABLE"),
    [countryPayers]
  );

  const offerablePayerCodes = useMemo(
    () => new Set(dedupeOfferableCashPayers(options)),
    [options]
  );

  const isAnyStateMode =
    !statesLoading &&
    !countryPayersLoading &&
    (states ?? []).length === 0 &&
    availableCountryPayers.length > 0;

  const [stateCode, setStateCode] = useState<string | null>(
    value?.stateCode ?? null
  );
  const [payerId, setPayerId] = useState<string | null>(value?.payerId ?? null);

  const offerablePayerCodesForState = useMemo(
    () => new Set(dedupeOfferableCashPayersForState(options, stateCode)),
    [options, stateCode]
  );

  // Drop subdivisions with no reachable payer: same gate the payer list and
  // Continuar apply, so the picker never leads to an empty payer select.
  // ANY-state mode is exempt — a country-wide SKU covers every subdivision.
  const stateOptions = useMemo(() => {
    const all = resolveStateOptions(destinationCountry, states, isAnyStateMode);
    if (isAnyStateMode || optionsLoading || offerablePayerCodes.size === 0) {
      return all;
    }
    const catalogPayersByState = new Map(
      (states ?? []).map((s) => [
        s.stateCode.toUpperCase(),
        s.payerCodes ?? [],
      ])
    );
    return all.filter((s) => {
      if (dedupeOfferableCashPayersForState(options, s.code).length > 0) {
        return true;
      }
      const catalogCodes = catalogPayersByState.get(s.code.toUpperCase()) ?? [];
      return catalogCodes.some((code) =>
        [...offerablePayerCodes].some((offerable) =>
          payerCodesEquivalent(offerable, code)
        )
      );
    });
  }, [
    destinationCountry,
    states,
    isAnyStateMode,
    optionsLoading,
    options,
    offerablePayerCodes,
  ]);

  useEffect(() => {
    if (!stateCode) return;
    if (stateOptions.length === 0) return;
    const stillOffered = stateOptions.some(
      (s) => s.code.toUpperCase() === stateCode.toUpperCase()
    );
    if (!stillOffered) {
      setStateCode(null);
      setPayerId(null);
    }
  }, [stateOptions, stateCode]);

  // Payers scoped to the chosen state (skipped in ANY-state mode — the backend
  // would equality-match stateCode and exclude the state-agnostic payers).
  const { data: scopedPayers, loading: scopedPayersLoading } = useCatalogPayers(
    !isAnyStateMode && stateCode
      ? {
          ...catalogScope,
          destinationCountryCode: destinationCountry,
          payoutMethod: "CASH_PICKUP",
          stateCode,
        }
      : null
  );

  const payerCatalog = useMemo(
    () => [...availableCountryPayers, ...(scopedPayers ?? [])],
    [availableCountryPayers, scopedPayers]
  );

  const payerList = useMemo(() => {
    if (optionsLoading || !stateCode) return [];
    const fromQuote = [...offerablePayerCodesForState];
    if (fromQuote.length > 0) {
      return fromQuote
        .map((code) => {
          const name = catalogPayerDisplayName(code, payerCatalog);
          return name ? { id: code, name } : null;
        })
        .filter((row): row is { id: string; name: string } => row != null)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const source = isAnyStateMode ? availableCountryPayers : scopedPayers ?? [];
    return source
      .filter((p) => p.effectiveStatus === "AVAILABLE")
      // Catalog availability is not quotability: a payer the pricing engine
      // cannot price would read as "route unavailable" right after the pick.
      .filter((p) =>
        [...offerablePayerCodes].some((code) =>
          payerCodesEquivalent(code, p.payerCode)
        )
      )
      .map((p) => {
        const name = catalogPayerDisplayName(p.payerCode, payerCatalog);
        return name ? { id: p.payerCode, name } : null;
      })
      .filter((row): row is { id: string; name: string } => row != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    optionsLoading,
    stateCode,
    offerablePayerCodesForState,
    payerCatalog,
    isAnyStateMode,
    availableCountryPayers,
    scopedPayers,
    offerablePayerCodes,
  ]);

  // A payer that isn't in the current list (e.g. after switching state) is
  // treated as unselected; the select reflects this and submit stays gated.
  const effectivePayerId = useMemo(() => {
    if (!payerId) return null;
    const exact = payerList.find((p) => p.id === payerId);
    if (exact) return exact.id;
    const aliased = payerList.find((p) =>
      payerCodesEquivalent(p.id, payerId)
    );
    return aliased?.id ?? null;
  }, [payerId, payerList]);

  const payersLoading =
    Boolean(optionsLoading) ||
    (isAnyStateMode
      ? countryPayersLoading
      : Boolean(stateCode) && scopedPayersLoading);

  const catalogCoversPayer = Boolean(
    effectivePayerId &&
      (isAnyStateMode ? availableCountryPayers : scopedPayers ?? []).some(
        (p) =>
          p.effectiveStatus === "AVAILABLE" &&
          payerCodesEquivalent(p.payerCode, effectivePayerId)
      )
  );

  const canResolveSku = Boolean(
    stateCode &&
      effectivePayerId &&
      resolveCashPickupOption(options, effectivePayerId, stateCode)
  );

  const valid = Boolean(canResolveSku || (stateCode && catalogCoversPayer));

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !stateCode || !effectivePayerId) return;
        onSubmit({ source: "new", stateCode, payerId: effectivePayerId });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.state")}
          value={stateCode ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={stateCode ?? ""}
            onChange={(e) => {
              setStateCode(e.target.value || null);
              // Switching state invalidates the previously-chosen payer.
              setPayerId(null);
            }}
          >
            <option value="" />
            {stateOptions.map((s) => (
              <option key={`${destinationCountry}-${s.code}`} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {!statesLoading && !optionsLoading && stateOptions.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noStates")}</span>
        )}
      </div>

      {stateCode && (
        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.payer")}
            value={effectivePayerId ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={effectivePayerId ?? ""}
              disabled={payersLoading}
              onChange={(e) => setPayerId(e.target.value || null)}
            >
              <option value="" />
              {payerList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {payersLoading && (
            <span className="payout-hint">{t("payout.fields.payerLoading")}</span>
          )}
          {!payersLoading && payerList.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noPayers")}</span>
          )}
        </div>
      )}

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};

/** Splits `MM/YY` typing as the user enters it. */
const formatCardExpiry = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const isExpiryValid = (expiry: string): boolean => {
  const m = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!m) return false;
  const month = Number(m[1]);
  return month >= 1 && month <= 12;
};

interface CardFormProps {
  value?: PayoutInput | null;
  sourceCountry: string;
  destinationCountry: string;
  onSubmit: (payout: PayoutInput) => void;
}

const CardForm: React.FC<CardFormProps> = ({
  value,
  sourceCountry,
  destinationCountry,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const linkId = useSessionStore((s) => s.session.data?.linkId ?? null);

  const [pan, setPan] = useState(value?.cardPan ?? "");
  const [expiry, setExpiry] = useState(value?.cardExpiry ?? "");
  const [cardholderName, setCardholderName] = useState(
    formatCardholderNameInput(value?.cardholderName ?? "")
  );
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const digits = pan.replace(/\D/g, "");
  const valid =
    digits.length >= 13 &&
    isExpiryValid(expiry) &&
    isValidCardholderName(cardholderName);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || submitting || !linkId) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const source = sourceCountry.trim().toUpperCase() || "US";
      const destination = destinationCountry.trim().toUpperCase() || "MX";
      const pre = await preValidatePayoutCard(linkId, {
        pan: digits,
        expiry,
        cardholderName: cardholderName.trim(),
        sourceCountry: source,
        destinationCountry: destination,
      });
      onSubmit({
        source: "new",
        cardExpiry: expiry,
        cardholderName: cardholderName.trim(),
        last4: pre.last4,
        brand: pre.brand,
        cvsValidationId: pre.validationId,
        numberToken: pre.numberToken,
        validatedFor: {
          sourceCountry: source,
          destinationCountry: destination,
        },
      });
    } catch (error) {
      const code =
        error instanceof PayoutCardValidationError ? error.code : undefined;
      if (code === "CARD_PUSH_NOT_SUPPORTED") {
        setFormError(t("payout.card.pushNotSupported"));
      } else if (code === "CARD_CORRIDOR_CONTEXT_REQUIRED") {
        setFormError(t("payout.card.corridorContextRequired"));
      } else if (code === "CARD_UNDER_REVIEW") {
        setFormError(t("payout.card.underReview"));
      } else if (code === "CARD_VALIDATION_FAILED") {
        setFormError(t("payout.card.validationFailed"));
      } else if (code === "VGS_CONFIG_UNAVAILABLE") {
        setFormError(t("vgs.configUnavailable"));
      } else {
        setFormError(t("payout.card.genericError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Send is third-party only: vault destination cards ("my cards") stay in m2m.
  // Replay for this contact comes from SavedDestinationsPicker (recipient book).
  return (
    <form className="payout-form" onSubmit={handleContinue}>
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardholderName")}
          value={cardholderName}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            style={{ color: bodyCopy }}
            value={cardholderName}
            disabled={submitting}
            maxLength={CARDHOLDER_NAME_INPUT_MAX_LENGTH}
            autoComplete="cc-name"
            autoCapitalize="characters"
            spellCheck={false}
            onChange={(e) =>
              setCardholderName(formatCardholderNameInput(e.target.value))
            }
          />
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardPan")}
          placeholder={t("payout.fields.cardPanPlaceholder")}
          value={groupDigits(digits)}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={groupDigits(digits)}
            disabled={submitting}
            onChange={(e) => setPan(sanitizeDigits(e.target.value, 19))}
          />
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardExpiry")}
          placeholder={t("payout.fields.cardExpiryPlaceholder")}
          value={expiry}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            maxLength={5}
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={expiry}
            disabled={submitting}
            onChange={(e) => setExpiry(formatCardExpiry(e.target.value))}
          />
        </FloatingField>
      </div>

      {formError ? (
        <p role="alert" style={{ color: "#dc251c", fontSize: "14px" }}>
          {formError}
        </p>
      ) : null}

      <Button
        type="submit"
        size="large"
        disabled={!valid || submitting}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {submitting ? t("transfer.prepare.loading") : t("common.continue")}
      </Button>
    </form>
  );
};

const MobileWalletForm: React.FC<SubFormProps> = ({
  destinationCountry,
  destinationCurrency,
  fundingMethod,
  originCountryCode,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const { data: payers } = useCatalogPayers({
    ...catalogScopeOf(fundingMethod, originCountryCode, destinationCurrency),
    destinationCountryCode: destinationCountry,
    payoutMethod: "MOBILE_WALLET",
  });
  const carriers = useMemo(
    () =>
      (payers ?? [])
        .filter((p) => p.effectiveStatus === "AVAILABLE" && p.displayName?.trim())
        .map((p) => ({ id: p.payerCode, name: p.displayName.trim() }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [payers]
  );

  const [carrierId, setCarrierId] = useState<string | null>(
    value?.carrierId ?? null
  );
  const [phone, setPhone] = useState(value?.phone ?? "");

  const valid = Boolean(carrierId) && phone.replace(/\D/g, "").length >= 8;

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !carrierId) return;
        onSubmit({ source: "new", carrierId, phone });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.carrier")}
          value={carrierId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={carrierId ?? ""}
            onChange={(e) => setCarrierId(e.target.value || null)}
          >
            <option value="" />
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.phone")}
          value={phone}
          colors={fieldColors}
        >
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </FloatingField>
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};
